import { RewardsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as RewardsActions from './rewards.actions';

@Injectable()
export class RewardsEffects {
  private actions$ = inject(Actions);
  private rewardsApi = inject(RewardsApiService);
  private notification = inject(NotificationService);

  loadRewards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardsActions.loadRewards),
      switchMap(({ params }) =>
        this.rewardsApi.findAll(params).pipe(
          map(response => RewardsActions.loadRewardsSuccess({
            rewards: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_rewards_error');
            return of(RewardsActions.loadRewardsFailure({ error: error.message || 'Failed to load rewards' }));
          }),
        ),
      ),
    ),
  );

  createReward$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardsActions.createReward),
      switchMap(({ rewardData }) =>
        this.rewardsApi.create(rewardData).pipe(
          map((reward) => {
            this.notification.success('admin.notifications.reward_created');
            return RewardsActions.createRewardSuccess({ reward });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_reward_error');
            return of(RewardsActions.createRewardFailure({ error: error.message || 'Failed to create reward' }));
          }),
        ),
      ),
    ),
  );

  updateReward$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardsActions.updateReward),
      switchMap(({ id, rewardData }) =>
        this.rewardsApi.update(id, rewardData).pipe(
          map((reward) => {
            this.notification.success('admin.notifications.reward_updated');
            return RewardsActions.updateRewardSuccess({ reward });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_reward_error');
            return of(RewardsActions.updateRewardFailure({ error: error.message || 'Failed to update reward' }));
          }),
        ),
      ),
    ),
  );

  deleteReward$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardsActions.deleteReward),
      switchMap(({ id }) =>
        this.rewardsApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.reward_deleted');
            return RewardsActions.deleteRewardSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_reward_error');
            return of(RewardsActions.deleteRewardFailure({ error: error.message || 'Failed to delete reward' }));
          }),
        ),
      ),
    ),
  );
}
